import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { stepPropTypes, stepDefaultProps } from './stepProps';

// Which pricelist supplies the sheets. No longer a step: it is the Advanced
// disclosure inside the contents step, for a photo package that needs to sell from
// a pricelist that already exists. The default path never asks -- PackageBuilder
// creates one on demand when the first purchase choice is added.
//
// Two modes, because "pick an existing one" had nothing to pick for a new season:
// seniors2028 has zero packages and zero options, and the admin had to leave the
// wizard for a legacy form whose every omission fails silently.
//
// The list offers only packages the STORE would sell for this season. It used to
// offer every senior/yearbook package -- 75, of which 3 were sellable -- so the
// likeliest outcome of using it was a product that read Ready and reached nobody.
// Only a photo package groups SEVERAL physical items into the choices a customer
// picks between ("two 8x10s and eight wallets"). A single print or a download is
// one item, and a personalised item is designed in Printess — but all of them still
// hang off a pricelist, because that is what the order and the invoice are
// built from. Saying "grouped into the choices a customer picks between" to someone
// setting up a single print is just confusing.
const WHAT_IT_IS = {
  package: 'A pricelist is the lab’s side of what you sell: the physical items it '
    + 'can produce — 8×10s, 5×7s, wallets, downloads — grouped into the choices a '
    + 'customer picks between. The shop reads the prints, the sheet counts and the '
    + 'shipping from it.',
  print: 'A pricelist is the lab’s side of what you sell. This product is a single '
    + 'print, so its package holds one item: the size the customer receives.',
  digital: 'A pricelist is the lab’s side of what you sell. This product is a '
    + 'download, so its package holds one item and nothing is printed or shipped — but '
    + 'the order is still built from it.',
  printess: 'A personalised item is designed by the customer in the Printess editor, so '
    + 'there are no sheets to choose. It still needs a pricelist: that is what the '
    + 'order, the invoice and the school’s access are built from.',
};

const Pricelist = ({
  product, choices, commit, busy,
}) => {
  const canCreate = choices.can_create_package;
  const hasExisting = choices.packages.length > 0;
  // Locked once packages are built on the current package: replacing it would
  // leave them pointing at the old one. The server refuses it as well.
  const locked = choices.can_replace_package === false;
  const [mode, setMode] = useState(hasExisting ? 'existing' : 'create');
  const [name, setName] = useState(product.name || '');
  const chosen = choices.packages.find((p) => p.id === product.legacy_package_id);
  const linked = product.variants.filter((v) => v.legacy_option_id).length;

  return (
    <Stack spacing={2}>
      {/* The wizard's whole premise is that ops should not have to know the data
          model, and "pricelist" is the one piece of it they cannot avoid --
          every later step reads from it. So it gets said in one plain sentence. */}
      <Typography variant="body2" color="text.secondary">
        {WHAT_IT_IS[product.product_type] || WHAT_IT_IS.package}
      </Typography>

      {product.legacy_package_name ? (
        <Alert severity="success">
          {`Prints come from ${product.legacy_package_name}.`}
        </Alert>
      ) : null}

      {locked ? (
        <Alert severity="info">
          {`This product sells ${linked} package${linked === 1 ? '' : 's'} built from `}
          {product.legacy_package_name || 'this package'}
          {'. The pricelist is fixed while those exist — changing it would leave '
            + 'them pointing at the old one and break every order. Remove them on '
            + '“What’s in it” first if you really need to switch.'}
        </Alert>
      ) : null}

      {canCreate && !locked ? (
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel
            value="existing"
            disabled={!hasExisting}
            control={<Radio />}
            label={(
              <Typography variant="body2">
                Use a pricelist that already exists
                {hasExisting ? '' : ' — none the store could sell for this season'}
              </Typography>
            )}
          />
          <FormControlLabel
            value="create"
            control={<Radio />}
            label={(
              <Typography variant="body2">
                {product.legacy_package_name
                  ? 'Set up a different one — replaces the empty one above'
                  : 'Set one up now — an empty package you fill in on the next step'}
              </Typography>
            )}
          />
        </RadioGroup>
      ) : null}

      {mode === 'existing' && hasExisting && !locked ? (
        <TextField
          select
          label="Which pricelist supplies the prints?"
          value={product.legacy_package_id || ''}
          onChange={(e) => commit({
            // print_package tells the merged step this is a package request, not a
            // buy-option one -- needed because CLEARING sends an empty value, and a
            // blank is not `present?`.
            print_package: '1',
            shop_product: { legacy_package_id: e.target.value },
          })}
          helperText={'Defines the physical sheets that get printed and shipped. Only '
            + `packages the store can sell for ${product.season || 'this season'} are listed.`}
        >
          <MenuItem value="">— choose a pricelist —</MenuItem>
          {choices.packages.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {`${p.name}${p.season ? ` · ${p.season}` : ''}`}
            </MenuItem>
          ))}
        </TextField>
      ) : null}

      {mode === 'create' && !locked ? (
        <Box>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              label="Name for the pricelist"
              value={name}
              sx={{ flex: '1 1 320px' }}
              onChange={(e) => setName(e.target.value)}
              helperText={'Name it after what the customer is buying. It cannot contain '
                + '“sitting”, “session” or “yearbook” — the store never sells those as '
                + 'photo products.'}
            />
            <Button
              variant="contained"
              disabled={busy || !name.trim()}
              sx={{ mt: 0.5 }}
              onClick={() => commit({ create_package: '1', package_name: name })}
            >
              {product.legacy_package_name ? 'Replace' : 'Create'}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {`Created for ${product.season || 'this season'}, hidden from the legacy `
              + 'pricelists, and sold only through the shop. You add what is in it on the '
              + 'next step.'}
          </Typography>
        </Box>
      ) : null}

      {!canCreate && !hasExisting ? (
        <Alert severity="warning">
          {`No pricelist exists that the store could sell for ${product.season || 'this season'}, `
            + 'and this product type cannot have one created. Set a season first.'}
        </Alert>
      ) : null}

      {chosen ? (
        <Paper>
          <Typography variant="subtitle2" sx={{ px: 2, py: 1.25, bgcolor: 'action.hover' }}>
            {`${chosen.name} contains`}
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Option</TableCell>
                <TableCell>Poses included</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chosen.options.map((o) => (
                <TableRow key={o.id}>
                  <TableCell>{o.name || `Option ${o.id}`}</TableCell>
                  <TableCell>{o.poses}</TableCell>
                </TableRow>
              ))}
              {!chosen.options.length ? (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography variant="caption" color="text.secondary">
                      Nothing in it yet — add what the customer gets on the next step.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Paper>
      ) : null}
    </Stack>
  );
};

Pricelist.propTypes = stepPropTypes;
Pricelist.defaultProps = stepDefaultProps;

export default Pricelist;
